export function formatCurrency(amount: number): string {
  const formattedNumber = Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}€`;
}

/** Currency with cents (2 decimals), space as thousands separator, dot as decimal separator, € suffix. */
export function formatCurrencyDecimals(amount: number): string {
  const n = Math.round(amount * 100) / 100;
  const [intRaw, frac] = n.toFixed(2).split('.');
  const intPart = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${intPart}.${frac}€`;
}

/**
 * Realised amount in edit inputs: dot decimal, no thousands separator, always 2 fractional digits.
 */
export function formatRealisedAmountInputValue(amount: number | null | undefined): string {
  if (amount == null) return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

/** Normalise raw realised input: comma → dot, strip junk, single decimal point (for controlled text fields). */
export function normaliseRealisedAmountInput(raw: string): string {
  let v = raw.replace(/,/g, '.');
  v = v.replace(/[^\d.]/g, '');
  const dot = v.indexOf('.');
  if (dot === -1) return v;
  return v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
}

/** Parse normalised realised text to a number, or null if empty / incomplete. */
export function parseRealisedAmountInputToNumber(s: string): number | null {
  const t = s.trim();
  if (t === '' || t === '.') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Renders YYYY-MM-DD only (strips time from ISO strings like 2026-02-12T01:00:00). */
export function formatDateOnly(value: string | null | undefined): string {
  if (value == null || value === '') return '-';
  const s = String(value).trim();
  const isoDate = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDate) return isoDate[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Localized date + time for timestamps (e.g. campaign email events). */
export function formatDateTime(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Human-readable month + year from revenue campaign `period_label` (YYYY-MM from API).
 * e.g. "2026-03" → "March 2026" (month name follows user locale). Uses UTC to avoid TZ shifts.
 */
export function formatPeriodLabelMonthYear(periodLabel: string | null | undefined): string | null {
  if (periodLabel == null || periodLabel === '') return null;
  const m = String(periodLabel).trim().match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) return null;
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, monthIndex, 1)),
  );
}

/** Parse nights from API (numeric, string, null). Rounds to integer; invalid or negative → null. */
export function coerceGroupNightCount(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return null;
    const num = Number(t.replace(',', '.'));
    if (!Number.isFinite(num) || num < 0) return null;
    return Math.round(num);
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.round(value);
  }
  if (typeof value === 'bigint') {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n);
  }
  return null;
}

/** UTC calendar-day index from YYYY-MM-DD (stable vs parseIso in local TZ). */
function utcDayIndexFromYmd(ymd: string): number | null {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  return Math.floor(Date.UTC(y, mo, d) / 86400000);
}

/**
 * User-facing nights column: prefer `number_of_nights` when valid; else nights between
 * check-in and check-out (date-only, checkout day excluded as length of stay).
 */
export function formatGroupNightsLabel(
  numberOfNights: unknown,
  checkinDate: string | null | undefined,
  checkoutDate: string | null | undefined,
): string {
  const fromField = coerceGroupNightCount(numberOfNights);
  if (fromField != null) {
    return String(fromField);
  }
  const ci = formatDateOnly(checkinDate);
  const co = formatDateOnly(checkoutDate);
  if (ci === '-' || co === '-') {
    return '—';
  }
  const a = utcDayIndexFromYmd(ci);
  const b = utcDayIndexFromYmd(co);
  if (a == null || b == null || b <= a) {
    return '—';
  }
  return String(b - a);
}

/** Join key for `mv_group_enriched_summary` rows ↔ `revenue_collection_responses` (handles date vs ISO). */
export function revenueResponseRowKey(requestId: number, checkinDate: string | null | undefined): string {
  if (checkinDate == null || checkinDate === '') {
    return `${requestId}:`;
  }
  return `${requestId}:${formatDateOnly(checkinDate)}`;
}

const LETTER = /[A-Za-zÀ-ÖØ-öø-ÿ]/u;

/**
 * Short initials from a display name (e.g. request manager). Tooltip should show the full name.
 * Two-word names: first + last initial; single token: up to two letters.
 */
export function formatManagerInitials(name: string | null | undefined): string {
  const t = name?.trim();
  if (!t) return '—';
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) {
    const lettersOnly = parts[0].match(new RegExp(LETTER.source, 'gu'));
    if (!lettersOnly?.length) return '—';
    const s =
      lettersOnly.length >= 2
        ? `${lettersOnly[0]}${lettersOnly[1]}`
        : `${lettersOnly[0]}${lettersOnly[0]}`;
    return s.toUpperCase();
  }
  const first = parts[0].match(LETTER)?.[0];
  const last = parts[parts.length - 1].match(LETTER)?.[0];
  if (!first || !last) return '—';
  return `${first}${last}`.toUpperCase();
}
